export default function LevelEditor() {
  return (
    <section id="level-editor">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Level Editor</h2>
      <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
        The Level Editor is the first tab you see when you open a world. It contains all the core
        settings stored in your world's level.dat file, split across several sub-tabs.
      </p>

      {/* World Info */}
      <div id="world-info" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">World Info</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>Basic information about your world.</p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">World Name</strong> — The display name shown in the Minecraft world list. You can rename your world here.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Seed</strong> — The world generation seed. You can change this — any newly generated chunks will use the new seed, so be careful.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">World Type</strong> — Shows whether the world is Infinite, Flat, or Legacy (Old). This determines how terrain generates when you explore new areas.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Game Mode</strong> — The default game mode for the world: Survival, Creative, Adventure, or Spectator.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Difficulty</strong> — World difficulty: Peaceful, Easy, Normal, or Hard.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">World Path</strong> — The location of the world on your computer, with a button to open the folder in your file browser.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">World Size</strong> — The total size of the world on disk.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Last Opened In</strong> — The version of Minecraft the world was last opened in. For information only.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">World Icon</strong> — A preview of the world's thumbnail image as it appears in the Minecraft world list. You can change this to any image by clicking the Change Icon button and browsing for a valid image file.</li>
          </ul>
        </div>
      </div>

      {/* Gameplay Settings */}
      <div id="gameplay-settings" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Gameplay Settings</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>Controls where players spawn and various world-level gameplay options.</p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">Spawn Coordinates (X, Y, Z)</strong> — The exact position where new players spawn and where players respawn after dying (if they haven't set a bed spawn). Setting the Spawn Y to "Auto-detect" will spawn the player on the first safe Y position the world finds.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Spawn Radius</strong> — How far from the spawn point players can randomly appear. Set to 0 for an exact spawn point.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Hardcore Mode</strong> — When enabled, players cannot respawn after death.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Force Game Mode</strong> — Forces all players into the world's default game mode when they join.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Chunk Tick Range</strong> — Controls the distance (in chunks) where random ticks occur. Higher values mean more active world simulation but can impact performance.</li>
          </ul>
          <p>
            There are more settings available on this tab — hover over the tooltips next to each
            setting for a description of what it does.
          </p>
        </div>
      </div>

      {/* Game Rules */}
      <div id="game-rules" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Game Rules</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-4 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Toggle individual game rules on or off. These are the same rules you can set with the
            <code className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-xs">/gamerule</code> command,
            but presented as simple checkboxes grouped into categories.
          </p>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Commands & Cheats</h4>
            <p>Settings related to commands, command blocks, and chat. Includes options like whether command blocks are enabled and whether command output is shown in chat.</p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">World Mechanics</h4>
            <p>Controls for world behaviour like daylight cycle, weather cycle, mob spawning, fire spread, TNT explosions, and mob griefing.</p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Drops & Damage</h4>
            <p>Options for item drops and damage rules. Includes settings like whether entities drop loot, block drops, and fall damage.</p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Player Settings</h4>
            <p>Player-specific rules like whether players keep inventory on death, respawn immediately, and show death messages.</p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Advanced</h4>
            <p>
              Numeric game rules: <strong>Random Tick Speed</strong> (controls crop growth, leaf decay, etc. — default is 1) and
              <strong> Players Sleeping Percentage</strong> (how many players need to sleep to skip the night — default is 100%).
            </p>
          </div>
        </div>
      </div>

      {/* Experiments */}
      <div id="experiments" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Experiments</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Enable or disable experimental features for your world. These are features that Mojang is
            testing before including them in a full release.
          </p>
          <p>
            Normally, once experiments are enabled in Minecraft they cannot be turned off. Bedrock
            Toolchest lets you disable them — although doing so may lead to unexpected results if
            your world already depends on experimental features.
          </p>
          <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
            <p className="text-amber-700 dark:text-amber-400 text-xs">
              Enabling or disabling experiments may change world behaviour. Make sure you have a
              backup before making changes.
            </p>
          </div>
          <p>Available experiments vary by Minecraft version. Current toggles include:</p>
          <ul className="space-y-1">
            <li>Villager Trades Rebalance</li>
            <li>Upcoming Creator Features</li>
            <li>Beta APIs</li>
            <li>Experimental Creator Camera Features</li>
            <li>Furnace Recipe Book</li>
            <li>Experimental Voxel Shape Features</li>
          </ul>
        </div>
      </div>

      {/* Default Abilities */}
      <div id="default-abilities" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Default Abilities</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Set the default abilities for new players joining the world. These abilities are applied
            to every player unless overridden per-player in the Player Editor.
          </p>
          <ul className="space-y-1">
            <li><strong className="text-slate-800 dark:text-slate-200">Attack Mobs / Attack Players</strong> — Whether players can deal damage to mobs or other players.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Build / Mine</strong> — Whether players can place or break blocks.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Use Redstone</strong> — Whether players can interact with doors, switches, and redstone components.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Instabuild</strong> — Blocks break instantly and don't consume items (like Creative mode).</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Invulnerable</strong> — The player cannot take damage.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">May Fly</strong> — Whether the player can fly.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Operator</strong> — Grants operator-level permissions.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Open Containers</strong> — Whether the player can open chests, barrels, and other containers.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Teleport</strong> — Whether the player can use teleportation.</li>
          </ul>
          <p>You can also adjust the default <strong>Walk Speed</strong> and <strong>Fly Speed</strong> for players.</p>
        </div>
      </div>

      {/* Flat World Editor */}
      <div id="flat-world-editor" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Flat World Editor</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            If your world type is set to Flat, this tab lets you customize the block layers that
            make up the terrain. Only available for flat worlds — a warning is shown if the world
            uses a different world type.
          </p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">Block Layers</strong> — Each layer is a block type and a count (thickness in blocks). Layers are displayed top-to-bottom. You can add, remove, and reorder layers.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Block Selection</strong> — Choose from any Minecraft block using a searchable dropdown with block icons.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Biome</strong> — Set the biome for the flat world. This affects things like grass colour, mob spawning, and weather.</li>
          </ul>
          <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-3">
            <p className="text-blue-700 dark:text-blue-400 text-xs">
              Changes to flat world layers only affect newly generated chunks. Chunks you've already
              explored will keep their existing terrain.
            </p>
          </div>
        </div>
      </div>

      {/* Advanced NBT Editor */}
      <div id="advanced-nbt" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Advanced NBT Editor</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            A tree-based browser for the raw level.dat NBT data. This shows every value stored in
            your world's level file, including settings that don't have a dedicated editor elsewhere
            in the program.
          </p>
          <ul className="space-y-2">
            <li>Browse all properties and values stored in your world's level data</li>
            <li>Expand compound tags and arrays to see and edit the values inside</li>
            <li>Double-click values (strings, numbers, booleans) to edit them directly</li>
          </ul>
          <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
            <p className="text-amber-700 dark:text-amber-400 text-xs">
              This is a power-user feature. Editing values here without understanding what they do
              can break your world. Always make sure you have a backup.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
